import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { validateNewPassword } from '@/lib/password-validation';
import { usePasswordResetMutation } from '@/queries/auth/usePasswordResetMutation';
import { useAuthStore } from '@/stores/auth-store';
import PasswordField from './PasswordField';
import PasswordRequirements from './PasswordRequirements';

const EMAIL_RE = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+$/;

function useQueryEmail() {
  const { search } = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('email') ?? '';
  }, [search]);
}

export default function ResetPassword() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const presetEmail = useQueryEmail();
  const resetPassword = usePasswordResetMutation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: presetEmail,
      confirmationCode: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (values) => {
    try {
      await resetPassword.mutateAsync({
        email: values.email,
        confirmationCode: values.confirmationCode,
        newPassword: values.newPassword,
      });
    } catch {
      /* notification from mutation */
    }
  };

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  const busy = isSubmitting || resetPassword.isPending;

  return (
    <div className="sign-in-panel mx-auto w-full max-w-[450px] px-2 text-center">
      <div className="glass-panel glass-panel-interactive p-8 sm:p-10">
        <h1 className="mb-2 text-[clamp(1.35rem,2vw+0.8rem,1.85rem)] font-bold text-[#121212]">
          Reset Password
        </h1>
        <p className="mb-8 text-left text-sm leading-relaxed text-[var(--festy-muted-text)]">
          Enter the email address for your account and the confirmation code
          from your inbox, then choose a new password.
        </p>
        <form
          className="admin-form text-left"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="input-group">
            <label htmlFor="reset-email">Email Address</label>
            <input
              id="reset-email"
              type="email"
              placeholder="email@address.com"
              autoComplete="email"
              {...register('email', {
                required: 'Required',
                validate: (v) =>
                  EMAIL_RE.test(v?.trim() ?? '') ||
                  'Enter a valid email address',
              })}
            />
            {errors.email ? (
              <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                {errors.email.message}
              </div>
            ) : null}
          </div>
          <div className="input-group">
            <label htmlFor="reset-code">Confirmation Code</label>
            <input
              id="reset-code"
              type="text"
              inputMode="numeric"
              placeholder="4-digit code"
              autoComplete="one-time-code"
              {...register('confirmationCode', {
                required: 'Required',
                validate: (v) =>
                  /^\d{4}$/.test(v?.trim() ?? '') ||
                  'Enter the 4-digit code from your email',
              })}
            />
            {errors.confirmationCode ? (
              <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                {errors.confirmationCode.message}
              </div>
            ) : null}
          </div>
          <PasswordField
            id="reset-new"
            label="New Password"
            placeholder="Min. 8 characters"
            autoComplete="new-password"
            register={register('newPassword', {
              validate: validateNewPassword,
            })}
            error={errors.newPassword?.message}
          />
          <PasswordRequirements password={newPassword} />
          <PasswordField
            id="reset-confirm"
            label="Confirm New Password"
            placeholder="Repeat new password"
            autoComplete="new-password"
            register={register('confirmPassword', {
              required: 'Required',
              validate: (v, formValues) =>
                v === formValues.newPassword || 'Passwords do not match',
            })}
            error={errors.confirmPassword?.message}
            className="mt-4"
          />
          <button
            type="submit"
            disabled={busy}
            className="mt-6 inline-flex w-full min-h-[52px] items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] bg-[var(--bethere-accent)] font-bold text-[#121212] shadow-sm transition hover:bg-[var(--bethere-secondary)] hover:shadow-md disabled:opacity-60"
          >
            <FontAwesomeIcon icon={['fas', 'key']} />
            Update Password
          </button>
          <p className="mt-6 text-center text-sm text-[var(--festy-muted-text)]">
            <Link
              to="/login"
              className="font-bold text-[var(--bethere-primary)] hover:underline"
            >
              Back to sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
